import { create } from 'zustand';
import { Message } from '@/types/chat'; // Import the shared type

// Define the shape of the store's state
interface ChatState {
  isChatOpen: boolean;
  messages: Message[];
  sessionId: string | null;
  persistentUserId: string | null;
  isInitialized: boolean; // To track if initial state is loaded
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  setSessionId: (id: string | null) => void; // Allow setting null session ID
  setPersistentUserId: (id: string) => void;
  initializeChatState: () => void; // Action to load initial state
  clearChat: () => void; // Action to clear chat for a new session
}

// Define localStorage keys
const LOCAL_STORAGE_USER_ID_KEY = 'chat_persistent_user_id'
const LOCAL_STORAGE_SESSION_KEY = 'chat_session_id'
const LOCAL_STORAGE_CHAT_HISTORY_KEY = 'chat_history'
const LOCAL_STORAGE_IS_OPEN_KEY = 'chat_is_open' // New key for open state

// Helper to safely stringify JSON for localStorage
const safeJsonStringify = (value: any): string | null => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.error("[Zustand] Error stringifying value for localStorage:", error);
    return null;
  }
};

// Create the Zustand store WITHOUT persist middleware
export const useChatStore = create<ChatState>((set, get) => {
  // Add visibility change listener inside the store
  if (typeof window !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        console.log('[ChatStore] Tab visible - checking for state sync');
        // Trigger state sync after a short delay
        setTimeout(() => {
          const state = get();
          if (state.isInitialized) {
            state.initializeChatState();
          }
        }, 100);
      }
    });

    // Add storage event listener for cross-tab synchronization
    window.addEventListener('storage', (e) => {
      const state = get();
      
      if (e.key === LOCAL_STORAGE_CHAT_HISTORY_KEY && e.newValue) {
        try {
          const newMessages = JSON.parse(e.newValue);
          console.log('[ChatStore] Cross-tab sync: messages updated in another tab');
          set({ messages: newMessages });
        } catch (error) {
          console.error('[ChatStore] Error syncing messages from another tab:', error);
        }
      }
      
      if (e.key === LOCAL_STORAGE_SESSION_KEY && e.newValue) {
        console.log('[ChatStore] Cross-tab sync: session ID updated in another tab');
        set({ sessionId: e.newValue });
      }
      
      if (e.key === LOCAL_STORAGE_IS_OPEN_KEY && e.newValue) {
        const isOpen = e.newValue === 'true';
        console.log('[ChatStore] Cross-tab sync: chat open state updated in another tab:', isOpen);
        set({ isChatOpen: isOpen });
      }
    });
  }

  return {
    // Initial state values (isChatOpen defaults to false, will be loaded)
    isChatOpen: false,
    messages: [
      {
        role: 'assistant',
        content: 'Hello! How can I help you today?',
        timestamp: new Date().toISOString() // Add initial timestamp
      },
    ],
    sessionId: null,
    persistentUserId: null,
    isInitialized: false,

    // --- Actions --- 
    
    // Actions to control open state (NOW handle localStorage)
    openChat: () => {
      set({ isChatOpen: true });
      if (typeof window !== 'undefined') {
        localStorage.setItem(LOCAL_STORAGE_IS_OPEN_KEY, 'true');
        console.log('[Zustand openChat] Updated isChatOpen in localStorage: true');
      }
    },
    closeChat: () => {
      set({ isChatOpen: false });
      if (typeof window !== 'undefined') {
        localStorage.setItem(LOCAL_STORAGE_IS_OPEN_KEY, 'false');
        console.log('[Zustand closeChat] Updated isChatOpen in localStorage: false');
      }
    },
    toggleChat: () => {
      const newState = !get().isChatOpen;
      set({ isChatOpen: newState });
      if (typeof window !== 'undefined') {
        localStorage.setItem(LOCAL_STORAGE_IS_OPEN_KEY, newState.toString());
        console.log(`[Zustand toggleChat] Updated isChatOpen in localStorage: ${newState}`);
      }
    },

    // Action to completely replace messages (handles localStorage)
    setMessages: (messages) => {
      set({ messages });
      if (typeof window !== 'undefined') {
        const stringifiedMessages = safeJsonStringify(messages);
        if (stringifiedMessages) {
          localStorage.setItem(LOCAL_STORAGE_CHAT_HISTORY_KEY, stringifiedMessages);
           console.log('[Zustand setMessages] Updated messages in localStorage.');
        }
      }
    },

    // Action to add a single message (handles localStorage)
    addMessage: (message) => {
      const currentMessages = get().messages;
      const newMessages = [...currentMessages, message];
      set({ messages: newMessages });
      if (typeof window !== 'undefined') {
        const stringifiedMessages = safeJsonStringify(newMessages);
        if (stringifiedMessages) {
           localStorage.setItem(LOCAL_STORAGE_CHAT_HISTORY_KEY, stringifiedMessages);
           console.log('[Zustand addMessage] Updated messages in localStorage.');
        }
      }
    },

    // Action to set the session ID (handles localStorage)
    setSessionId: (id) => {
      set({ sessionId: id });
      if (typeof window !== 'undefined') {
         if (id === null) {
           localStorage.removeItem(LOCAL_STORAGE_SESSION_KEY);
           console.log('[Zustand setSessionId] Removed session ID from localStorage.');
         } else {
           localStorage.setItem(LOCAL_STORAGE_SESSION_KEY, id);
           console.log('[Zustand setSessionId] Updated session ID in localStorage:', id);
         }
      }
    },

    // Action to set the persistent user ID (handles localStorage)
    setPersistentUserId: (id) => {
       set({ persistentUserId: id });
       if (typeof window !== 'undefined') {
          localStorage.setItem(LOCAL_STORAGE_USER_ID_KEY, id);
          console.log('[Zustand setPersistentUserId] Updated user ID in localStorage:', id);
       }
    },

    // Action to initialize ALL state from localStorage - ALWAYS RELOAD with emergency fix
    initializeChatState: () => {
      // EMERGENCY FIX: ALWAYS reload state from localStorage, even if already initialized
      // Remove initialization check completely
      if (typeof window === 'undefined') {
        console.log('[Zustand initializeChatState] Running on server, can\'t initialize.');
        return;
      }
      
      console.log('[Zustand initializeChatState] EMERGENCY FIX: Always reinitializing ALL chat state from localStorage...');

      // Load ALL persisted values
      let userId = localStorage.getItem(LOCAL_STORAGE_USER_ID_KEY);
      let sessionId = localStorage.getItem(LOCAL_STORAGE_SESSION_KEY);
      let loadedMessages: Message[] | null = null;
      let isOpen = localStorage.getItem(LOCAL_STORAGE_IS_OPEN_KEY) === 'true'; // Load isChatOpen

      // Get/Set User ID
      if (!userId) {
        userId = 'user_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36);
        localStorage.setItem(LOCAL_STORAGE_USER_ID_KEY, userId);
        console.log('[Zustand initializeChatState] Generated new persistentUserId:', userId);
      } else {
        console.log('[Zustand initializeChatState] Loaded persistentUserId:', userId);
      }

      // Get/Set Session ID
      if (!sessionId) {
        sessionId = 'session_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36);
        localStorage.setItem(LOCAL_STORAGE_SESSION_KEY, sessionId);
        console.log('[Zustand initializeChatState] Generated new sessionId:', sessionId);
      } else {
        console.log('[Zustand initializeChatState] Loaded sessionId:', sessionId);
      }

      // Load Chat History - with more error handling
      let defaultMessages: Message[] = [
        {
          role: 'assistant' as const,
          content: 'Hello! How can I help you today?',
          timestamp: new Date().toISOString()
        }
      ];
      
      if (sessionId) {
        try {
          const savedHistory = localStorage.getItem(LOCAL_STORAGE_CHAT_HISTORY_KEY);
          if (savedHistory) {
            const parsedHistory = JSON.parse(savedHistory) as Message[];
            if (Array.isArray(parsedHistory) && parsedHistory.length > 0) {
              loadedMessages = parsedHistory;
              console.log('[Zustand initializeChatState] Loaded messages from history:', loadedMessages.length);
            } else { 
              console.warn('[Zustand initializeChatState] History existed but was empty or not an array');
            }
          } else { 
            console.warn('[Zustand initializeChatState] No saved history found'); 
          }
        } catch (error) {
          console.error('[Zustand initializeChatState] Error loading chat history - resetting:', error);
          localStorage.setItem(LOCAL_STORAGE_CHAT_HISTORY_KEY, JSON.stringify(defaultMessages));
          loadedMessages = defaultMessages;
        }
      }
      
      console.log(`[Zustand initializeChatState] Loaded isChatOpen state: ${isOpen}`);

      // EMERGENCY FIX: Forced full state replacement instead of conditional
      set({
        persistentUserId: userId,
        sessionId: sessionId,
        messages: loadedMessages || defaultMessages,
        isChatOpen: isOpen, 
        isInitialized: true, // Always mark as initialized
      });
      console.log('[Zustand initializeChatState] EMERGENCY: Complete state replacement done.');
    },

    // Action to start a new chat (handles localStorage)
    clearChat: () => {
      console.log('[Zustand clearChat] Clearing chat for new session...');
      const newSessionId = 'session_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36);
      const initialMessages: Message[] = [ // Ensure type is used here
        {
          role: 'assistant' as const,
          content: 'Hello! How can I help you today?',
          timestamp: new Date().toISOString() // Add initial timestamp
        }
      ];
      // Update state
      set({
        sessionId: newSessionId,
        messages: initialMessages,
        isChatOpen: true // Assume we want the chat open when starting anew?
      });
       // Update localStorage for all relevant keys
       if (typeof window !== 'undefined') {
          localStorage.setItem(LOCAL_STORAGE_SESSION_KEY, newSessionId);
          const stringifiedMessages = safeJsonStringify(initialMessages);
          if (stringifiedMessages) {
              localStorage.setItem(LOCAL_STORAGE_CHAT_HISTORY_KEY, stringifiedMessages);
          }
          localStorage.setItem(LOCAL_STORAGE_IS_OPEN_KEY, 'true'); // Persist open state
          console.log('[Zustand clearChat] New chat started & persisted. SessionId:', newSessionId);
       }
    },
  };
}); 